import { useEffect, useState } from 'react';

const INSTRUCTIONS_SEEN_KEY = 'loremail-instructions-seen';

interface InstructionsModalProps {
  forceOpen?: boolean;
  onClose?: () => void;
}

/**
 * Instructions modal explaining how to use Loremail
 * Shows automatically on first visit, can be reopened via button
 */
export function InstructionsModal({ forceOpen, onClose }: InstructionsModalProps) {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (forceOpen !== undefined) {
      setIsOpen(forceOpen);
      return;
    }

    // Show on first visit
    const hasSeenInstructions = localStorage.getItem(INSTRUCTIONS_SEEN_KEY);
    if (!hasSeenInstructions) {
      setIsOpen(true);
    }
  }, [forceOpen]);

  const handleDismiss = () => {
    setIsOpen(false);
    onClose?.();
  };

  const handleBegin = () => {
    localStorage.setItem(INSTRUCTIONS_SEEN_KEY, 'true');
    handleDismiss();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="absolute inset-0 bg-void/85 backdrop-blur-sm" onClick={handleDismiss} />
      <div className="relative w-full max-w-lg max-h-[90vh] sm:max-h-[85vh] flex flex-col bg-gradient-to-b from-night/95 to-void/95 border border-stone/30 rounded-xl backdrop-blur-sm shadow-2xl">
        {/* Decorative corner runes */}
        <div className="absolute top-2 left-2 text-rune/30 font-display text-xs">ᚠ</div>
        <div className="absolute top-2 right-2 text-rune/30 font-display text-xs">ᚢ</div>
        <div className="absolute bottom-2 left-2 text-rune/30 font-display text-xs">ᚦ</div>
        <div className="absolute bottom-2 right-2 text-rune/30 font-display text-xs">ᚨ</div>

        {/* Fixed Header */}
        <div className="flex-shrink-0 p-4 sm:p-6 pb-0">
          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 sm:top-4 sm:right-4 text-mist/50 hover:text-mist transition-colors p-1"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          <div className="text-center mb-4">
            <h2 className="font-display text-xl sm:text-2xl text-parchment mb-1">
              Welcome, Author
            </h2>
            <p className="text-rune text-sm italic">A living world that writes back</p>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 min-h-0">
          <div className="space-y-4 text-parchment/85 text-sm leading-relaxed">
            <section>
              <h3 className="font-display text-base text-parchment mb-1.5">What is Loremail?</h3>
              <p className="text-mist/80">
                Loremail is a storytelling service where you correspond with a fictional world. You
                send letters into it, and the world writes back. Over time, you become the author
                and architect of your own universe.
              </p>
            </section>

            <section>
              <h3 className="font-display text-base text-parchment mb-1.5">How It Works</h3>
              <ol className="space-y-1.5 text-mist/80 list-decimal list-inside">
                <li>
                  <span className="text-parchment/90">Send a letter</span> to your Correspondent
                  address. Describe characters, places, events, or atmosphere.
                </li>
                <li>
                  <span className="text-parchment/90">Your letter arrives</span> in the world. The
                  universe absorbs your words and evolves.
                </li>
                <li>
                  <span className="text-parchment/90">The world replies</span> once per day with a
                  letter written from inside your universe.
                </li>
              </ol>
            </section>

            <section>
              <h3 className="font-display text-base text-parchment mb-1.5">The Rhythm</h3>
              <p className="text-mist/80">
                This is not a chat. It is a slow, deliberate correspondence. One letter in, one
                letter back. The pacing creates anticipation and emotional weight.
              </p>
            </section>

            <section>
              <h3 className="font-display text-base text-parchment mb-1.5">You Are the Author</h3>
              <p className="text-mist/80">
                Write freely. Define the genre, the tone, the rules. Introduce characters and
                conflicts. The world will grow from your words.
              </p>
            </section>

            <div className="pt-3 border-t border-stone/30">
              <p className="text-center text-mist/60 text-xs italic">
                What if a world you imagined could write back to you?
              </p>
            </div>
          </div>
        </div>

        {/* Fixed Footer with CTA */}
        <div className="flex-shrink-0 p-4 sm:p-6 pt-4">
          <button
            onClick={handleBegin}
            className="w-full px-6 py-3 bg-rune hover:bg-rune-glow text-void font-medium rounded-lg text-sm transition-colors"
          >
            Begin Your Correspondence
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Button to reopen the instructions modal
 */
export function InstructionsButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="text-mist/30 hover:text-mist/50 text-xs transition-colors"
      title="How to use Loremail"
    >
      ?
    </button>
  );
}
